import { STATE_TO_IANA_TZ } from '@bonsai/shared';
import { useCallback, useEffect, useMemo, type Dispatch, type SetStateAction } from 'react';

import { soleRegion } from '../dashboards/workshops/create/workshopCreateRegions';

import {
  EMPTY_FIELDS,
  type PrivateEventFormFields,
  type PrivateEventLocationMode,
} from './private-event-form-state';
import {
  regionDefaultZone,
  toVenueOptions,
  venueInRegion,
  type VenueOption,
} from './private-event-venues';
import type { PrivateEventOptions } from './usePrivateEventOptions';

type SetRaw = Dispatch<SetStateAction<PrivateEventFormFields>>;

/**
 * Region, venue and time-zone behaviour of the create form, kept out of the
 * form hook so each stays small.
 *
 * The zone is derived rather than stored twice: while its source is `default`
 * it follows the state (client address) or the region's venues (to be
 * determined); once the RD picks one by hand it is theirs.
 */
export function usePrivateEventLocation(
  raw: PrivateEventFormFields,
  setRaw: SetRaw,
  options: PrivateEventOptions,
  edit?: { baseline: PrivateEventFormFields },
) {
  const { regions, venueRows } = options;
  const only = soleRegion(regions);
  const onlyId = only?.id ?? '';

  useEffect(() => {
    if (onlyId && !edit) setRaw((prev) => (prev.regionId ? prev : { ...prev, regionId: onlyId }));
  }, [onlyId, setRaw, edit]);

  const venueOptions = useMemo(
    () => toVenueOptions(venueRows, regions, raw.regionId),
    [venueRows, regions, raw.regionId],
  );

  const defaultZone =
    raw.locationMode === 'client-site'
      ? (STATE_TO_IANA_TZ[raw.state] ?? '')
      : raw.locationMode === 'tbd'
        ? regionDefaultZone(venueRows, regions, raw.regionId)
        : '';
  const timeZone = raw.timeZoneSource === 'manual' ? raw.timeZone : defaultZone;

  const fields = useMemo(() => ({ ...raw, timeZone }), [raw, timeZone]);

  const venueZone = venueOptions.find((v) => v.id === raw.venueId)?.timeZone ?? '';
  const eventZone = raw.locationMode === 'venue' ? venueZone : timeZone;

  const setRegion = useCallback(
    (regionId: string) => {
      const inRegion = toVenueOptions(venueRows, regions, regionId);
      setRaw((prev) => ({
        ...prev,
        regionId,
        venueId: venueInRegion(inRegion, prev.venueId) ? prev.venueId : '',
      }));
    },
    [venueRows, regions, setRaw],
  );

  const setLocationMode = useCallback(
    (locationMode: PrivateEventLocationMode) => setRaw((prev) => ({ ...prev, locationMode })),
    [setRaw],
  );

  const setTimeZone = useCallback(
    (zone: string) =>
      setRaw((prev) => ({
        ...prev,
        timeZone: zone,
        timeZoneSource: zone === '' ? 'default' : 'manual',
      })),
    [setRaw],
  );

  const isDirty = useMemo(() => {
    const baseline = edit?.baseline ?? { ...EMPTY_FIELDS, regionId: onlyId };
    return JSON.stringify(raw) !== JSON.stringify(baseline);
  }, [raw, onlyId, edit]);

  return {
    fields,
    venueOptions: venueOptions as VenueOption[],
    eventZone,
    setRegion,
    setLocationMode,
    setTimeZone,
    isDirty,
  };
}
